import { useEffect } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import { setNavigator } from "./lib/router-compat.jsx";
import RequireAuth from "./auth/RequireAuth.jsx";

// Public pages
import { HomePage, AboutPage, FounderPage, GirlPowerPage, PrivacyPage } from "./pages/public/SectionPages.jsx";
import { ProgramsPage, ProgramDetailPage } from "./pages/public/ProgramsPage.jsx";
import GalleryPage from "./pages/public/GalleryPage.jsx";
import ContactPage from "./pages/public/ContactPage.jsx";
import DonatePage, { DonateThankYouPage } from "./pages/public/DonatePage.jsx";
import { BlogIndexPage, BlogDetailPage } from "./pages/public/BlogPage.jsx";
import { EventsPage, EventDetailPage } from "./pages/public/EventsPage.jsx";
import RegisterClubPage from "./pages/public/RegisterClubPage.jsx";
import NotFoundPage from "./pages/public/NotFoundPage.jsx";

// Admin pages
import AdminSignInPage from "./pages/admin/AdminSignInPage.jsx";
import AdminDashboardPage from "./pages/admin/AdminDashboardPage.jsx";
import { AdminPagesListPage, AdminPageEditorPage } from "./pages/admin/AdminPagesPage.jsx";
import { AdminPostsListPage, AdminPostEditorPage } from "./pages/admin/AdminPostsPage.jsx";
import AdminGalleryPage from "./pages/admin/AdminGalleryPage.jsx";
import AdminProgramsPage from "./pages/admin/AdminProgramsPage.jsx";
import { AdminLeadsListPage, AdminLeadDetailPage } from "./pages/admin/AdminLeadsPage.jsx";
import { AdminEnquiriesListPage, AdminEnquiryDetailPage } from "./pages/admin/AdminEnquiriesPage.jsx";
import AdminDonationsPage from "./pages/admin/AdminDonationsPage.jsx";
import AdminSettingsPage from "./pages/admin/AdminSettingsPage.jsx";
import { AdminEventsListPage, AdminEventEditorPage, AdminEventPledgesPage } from "./pages/admin/AdminEventsPage.jsx";
import { AdminSchoolGroupsListPage, AdminSchoolGroupDetailPage } from "./pages/admin/AdminSchoolGroupsPage.jsx";

// Bridge so imperative navigate() (in form handlers) uses react-router.
function NavigatorBridge() {
  const navigate = useNavigate();
  useEffect(() => { setNavigator(navigate); }, [navigate]);
  return null;
}

const protect = (el) => <RequireAuth>{el}</RequireAuth>;

export default function App() {
  return (
    <>
      <NavigatorBridge />
      <Routes>
        {/* Public */}
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/founder" element={<FounderPage />} />
        <Route path="/programs" element={<ProgramsPage />} />
        <Route path="/programs/:slug" element={<ProgramDetailPage />} />
        <Route path="/girl-unlimited" element={<GirlPowerPage />} />
        <Route path="/girl-power" element={<GirlPowerPage />} />
        <Route path="/events" element={<EventsPage />} />
        <Route path="/events/:slug" element={<EventDetailPage />} />
        <Route path="/gallery" element={<GalleryPage />} />
        <Route path="/blog" element={<BlogIndexPage />} />
        <Route path="/blog/:slug" element={<BlogDetailPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/donate" element={<DonatePage />} />
        <Route path="/donate/thank-you" element={<DonateThankYouPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/register-club" element={<RegisterClubPage />} />

        {/* Admin */}
        <Route path="/admin/sign-in" element={<AdminSignInPage />} />
        <Route path="/admin" element={protect(<AdminDashboardPage />)} />
        <Route path="/admin/events" element={protect(<AdminEventsListPage />)} />
        <Route path="/admin/events/new" element={protect(<AdminEventEditorPage isNew />)} />
        <Route path="/admin/events/:eventId/edit" element={protect(<AdminEventEditorPage />)} />
        <Route path="/admin/events/:eventId/pledges" element={protect(<AdminEventPledgesPage />)} />
        <Route path="/admin/posts" element={protect(<AdminPostsListPage />)} />
        <Route path="/admin/posts/new" element={protect(<AdminPostEditorPage isNew />)} />
        <Route path="/admin/posts/:postId/edit" element={protect(<AdminPostEditorPage />)} />
        <Route path="/admin/pages" element={protect(<AdminPagesListPage />)} />
        <Route path="/admin/pages/:slug/edit" element={protect(<AdminPageEditorPage />)} />
        <Route path="/admin/programs" element={protect(<AdminProgramsPage />)} />
        <Route path="/admin/gallery" element={protect(<AdminGalleryPage />)} />
        <Route path="/admin/leads" element={protect(<AdminLeadsListPage />)} />
        <Route path="/admin/leads/:leadId" element={protect(<AdminLeadDetailPage />)} />
        <Route path="/admin/enquiries" element={protect(<AdminEnquiriesListPage />)} />
        <Route path="/admin/enquiries/:enquiryId" element={protect(<AdminEnquiryDetailPage />)} />
        <Route path="/admin/groups" element={protect(<AdminSchoolGroupsListPage />)} />
        <Route path="/admin/groups/:groupId" element={protect(<AdminSchoolGroupDetailPage />)} />
        <Route path="/admin/donations" element={protect(<AdminDonationsPage />)} />
        <Route path="/admin/settings" element={protect(<AdminSettingsPage />)} />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
}
